import React, { useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { UserContext } from '../App';
import NomineeShell from './NomineeShell';
import { Badge, Button, Card, Container, Stack, Text } from '../ui';
import { useNomineeEstates } from '../hooks/useNomineeEstates';
import { formatEstateDisplayName } from '../utils/nomineeEstate';

export default function NomineeHome() {
  const { currentUser, setCurrentUser } = useContext(UserContext);
  const { estates, loading, error } = useNomineeEstates();
  const navigate = useNavigate();

  if (!currentUser) return <div>Loading...</div>;
  if (currentUser.type !== 'nominee') {
    navigate('/dashboard');
    return null;
  }

  const openEstate = async (ownerSub, destination) => {
    try {
      // Persist last selected estate on the backend for nominee context.
      await api.post('/auth/switch-role', { active_context: 'nominee', active_owner_sub: ownerSub });
      const updated = { ...currentUser, activeOwnerSub: ownerSub, type: 'nominee' };
      setCurrentUser(updated);
      try { localStorage.setItem('currentUser', JSON.stringify(updated)); } catch {}
    } catch (_) {
      // Non-fatal; navigation can still proceed.
    }
    navigate(destination);
  };

  const activeOwnerSub = String(currentUser?.activeOwnerSub || '').trim();
  const activeEstate = activeOwnerSub ? estates.find((e) => e?.ownerSub === activeOwnerSub) : null;
  return (
    <NomineeShell>
      <div style={{ padding: '32px 0' }}>
        <Container>
          <Stack gap={16}>
            <Stack gap={6}>
              <Text as="h1" size="xl" weight={800} style={{ margin: 0 }}>
                Nominee Dashboard
              </Text>
              <Text muted style={{ margin: 0 }}>
                Estates will appear here once an owner has added you as a nominee and shared access with you.
              </Text>
            </Stack>

            {error && (
              <Card style={{ background: 'var(--peach-50)', borderColor: 'var(--peach-300)' }}>
                <div className="pv-error">{error}</div>
              </Card>
            )}

            {loading ? (
              <Text style={{ marginTop: 8 }}>Loading…</Text>
            ) : estates.length === 0 ? (
              <Card>
                <Text muted>No estates found yet.</Text>
              </Card>
            ) : activeEstate ? (() => {
                const finOk = !!activeEstate?.grants?.financial_summary;
                const legalOk = !!activeEstate?.grants?.legal_summary_non_sensitive;
                const otherOk = !!(
                  activeEstate?.grants?.other_assets_final_wishes
                  || activeEstate?.grants?.other_assets_physical_assets
                  || activeEstate?.grants?.other_assets_digital_assets
                  || activeEstate?.grants?.other_assets_personal_notes
                );
                return (
                  <Card>
                    <Stack gap={10}>
                      <Stack direction="row" gap={12} align="center" justify="space-between" wrap="wrap">
                        <Stack gap={6}>
                          <Text as="div" weight={700}>Active estate</Text>
                          <Text as="div" size="sm" style={{ margin: 0 }}>
                            {formatEstateDisplayName(activeEstate)}
                          </Text>
                        </Stack>
                        <Stack direction="row" gap={8} align="center" wrap="wrap">
                          <Badge>{finOk ? 'Financial shared' : 'Financial not shared'}</Badge>
                          <Badge>{legalOk ? 'Legal shared' : 'Legal not shared'}</Badge>
                        </Stack>
                      </Stack>

                      <Stack direction="row" gap={10} wrap="wrap">
                        <Button
                          type="button"
                          variant="primary"
                          disabled={!finOk}
                          onClick={() => openEstate(activeEstate.ownerSub, `/nominee/estate/${encodeURIComponent(activeEstate.ownerSub)}/financial`)}
                        >
                          View financial summary
                        </Button>
                        <Button
                          type="button"
                          variant="secondary"
                          disabled={!legalOk}
                          onClick={() => openEstate(activeEstate.ownerSub, `/nominee/estate/${encodeURIComponent(activeEstate.ownerSub)}/legal`)}
                        >
                          View legal summary
                        </Button>
                        <Button
                          type="button"
                          variant="secondary"
                          disabled={!otherOk}
                          onClick={() => openEstate(activeEstate.ownerSub, `/nominee/estate/${encodeURIComponent(activeEstate.ownerSub)}/other`)}
                        >
                          View other assets
                        </Button>
                      </Stack>

                      <Text size="sm" muted style={{ margin: 0 }}>
                        {!finOk && !legalOk && 'Nothing has been shared with you yet.'}
                        {finOk && !legalOk && 'Financial and other assets are shared. Legal summary is not shared.'}
                        {!finOk && legalOk && 'Legal summary is shared. Financial summary is not shared.'}
                        {finOk && legalOk && 'Financial, other assets, and legal summaries are shared.'}
                      </Text>
                    </Stack>
                  </Card>
                );
              })() : (
                <Card>
                  <Stack gap={10}>
                    <Text as="div" weight={700}>Choose an estate</Text>
                    <Text size="sm" muted style={{ margin: 0 }}>
                      Use the estate selector at the top of the page to pick the account owner whose information you need to see.
                      You can change estates at any time from that dropdown.
                    </Text>
                  </Stack>
                </Card>
              )
            }
          </Stack>
        </Container>
      </div>
    </NomineeShell>
  );
}
